/**
 * Collapsible MCP tool-activity panel, shared between the live streaming
 * view and the persisted message history (plan section 10, row 24). Renders
 * from a `steps` prop only -- it does not know or care whether the caller
 * got them from the in-flight SSE accumulator (`toolStepAccumulator.ts`) or
 * from a persisted message. Renders nothing when `steps` is absent/empty, so
 * a reloaded conversation (tool-step persistence lands in a later package)
 * simply shows no panel rather than an empty shell.
 *
 * Each step is its own collapsed-by-default row that expands to show tool
 * name, server key, arguments, status, and summary. `arguments`/`summary`
 * are rendered as plain text ONLY (never through the markdown renderer, never
 * as HTML) -- they are sanitized and length-capped server-side (S11), but
 * may still contain neutralized markers, which is expected.
 */

import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutlined as SucceededIcon,
  ErrorOutlined as FailedIcon,
  BlockOutlined as RefusedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ToolStep } from '../../api/data-contracts';

interface ToolStepsPanelProps {
  steps?: ToolStep[] | null;
}

const STATUS_ICON_SIZE = 16;

function StatusIcon({ status }: { status: ToolStep['status'] }): React.ReactElement {
  const theme = useTheme();

  switch (status) {
    case 'running':
      return <CircularProgress size={STATUS_ICON_SIZE} thickness={5} />;
    case 'succeeded':
      return <SucceededIcon sx={{ fontSize: STATUS_ICON_SIZE, color: theme.palette.success.main }} />;
    case 'failed':
      return <FailedIcon sx={{ fontSize: STATUS_ICON_SIZE, color: theme.palette.error.main }} />;
    case 'refused':
      return <RefusedIcon sx={{ fontSize: STATUS_ICON_SIZE, color: theme.palette.warning.main }} />;
    default:
      return <></>;
  }
}

const ToolStepRow: React.FC<{ step: ToolStep }> = ({ step }) => {
  const { t } = useTranslation();

  const headline = t(`chat.toolSteps.${step.status}`, { toolName: step.tool_name });
  const statusLabel = t(`chat.toolSteps.statusLabels.${step.status}`);

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        opacity: step.discarded ? 0.6 : 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 } }}
      >
        <StatusIcon status={step.status} />
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            textDecoration: step.discarded ? 'line-through' : 'none',
          }}
        >
          {headline}
        </Typography>
        {step.discarded && (
          <Chip
            size="small"
            label={t('chat.toolSteps.discarded')}
            color="default"
            variant="outlined"
          />
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('chat.toolSteps.fields.tool')}
            </Typography>
            <Typography variant="body2">{step.tool_name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('chat.toolSteps.fields.server')}
            </Typography>
            <Typography variant="body2">{step.server_key}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('chat.toolSteps.fields.arguments')}
            </Typography>
            {/* Plain text only -- never markdown/HTML (S11/S14). */}
            <Typography
              variant="body2"
              component="pre"
              sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }}
            >
              {step.arguments}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('chat.toolSteps.fields.status')}
            </Typography>
            <Typography variant="body2">{statusLabel}</Typography>
          </Box>
          {step.summary !== null && step.summary !== undefined && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('chat.toolSteps.fields.summary')}
              </Typography>
              {/* Plain text only -- never markdown/HTML (S11/S14). */}
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                }}
              >
                {step.summary}
              </Typography>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export const ToolStepsPanel: React.FC<ToolStepsPanelProps> = ({ steps }) => {
  if (!steps || steps.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.75} sx={{ width: '100%', mb: 1.5 }}>
      {steps.map((step) => (
        <ToolStepRow key={step.call_id} step={step} />
      ))}
    </Stack>
  );
};
